import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {useCallback} from "react";

import {writeStar} from "../../api/relationshipApi";
import {useViewer} from "../../context/ViewerContext";
import {useStarState} from "../../hooks/useRelationship";
import {gotoSignUp} from "../../utils/auth";

const STAR_BUTTON_SX = {
    textTransform: "none",
    padding: "4px 10px",
    borderColor: "divider",
    height: 30,
    "& .MuiTypography-caption": {fontSize: 13, fontWeight: 500},
    "& svg": {fontSize: 18, mr: "8px"},
} as const;

export default function StarButton(props: {documentUid: string}) {
    const {documentUid} = props;
    const {uid: viewerUid, authResolved} = useViewer();
    const {active, setOptimistic} = useStarState(documentUid, viewerUid);

    const starred = active === true;

    const onClick = useCallback(() => {
        if (!viewerUid) {
            gotoSignUp();
            return;
        }
        const next = !starred;
        setOptimistic(next);
        void writeStar(next ? "star" : "unstar", documentUid).catch(() => {
            setOptimistic(!next);
        });
    }, [viewerUid, starred, setOptimistic, documentUid]);

    // Matches the main app's StarButton, which renders nothing until the star
    // state is known instead of showing a disabled button.
    if ((viewerUid != null && active === undefined) || (viewerUid == null && !authResolved)) {
        return null;
    }

    return (
        <Button
            size="small"
            variant="outlined"
            onClick={onClick}
            sx={STAR_BUTTON_SX}
            data-cy={starred ? "unstarButton" : "starButton"}
        >
            {starred ? <StarIcon /> : <StarBorderIcon />}
            <Typography variant="caption" component="span">
                {starred ? "Unstar" : "Star"}
            </Typography>
        </Button>
    );
}
